import { useEffect, useRef, useState } from "react";
import {
  ANNEX_III_AUDIT_EVENTS,
  type AnnexIIIAuditRecord,
  getAnnexIIIAuditLogs,
  logAnnexIIIAuditEvent,
} from "~/lib/annex-iii-audit";

interface CandidateScreeningBadgeProps {
  candidateId?: string;
  jobId?: string;
  screeningScore?: number;
  compact?: boolean;
  className?: string;
  onHumanReviewRequested?: () => void;
}

/**
 * Candidate transparency UI badge and compliance panel for EU AI Act Annex III
 * high-risk recruitment, selection, and candidate-screening systems.
 */
export function CandidateScreeningBadge({
  candidateId = "current-candidate",
  jobId = "current-job",
  screeningScore,
  compact = false,
  className = "",
  onHumanReviewRequested,
}: CandidateScreeningBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AnnexIIIAuditRecord[]>([]);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    logAnnexIIIAuditEvent({
      candidateId,
      jobId,
      screeningScore,
      eventType: ANNEX_III_AUDIT_EVENTS.TRANSPARENCY_DISCLOSURE_VIEWED,
      disclosuresConfirmed: true,
    });
    setAuditLogs(getAnnexIIIAuditLogs());
  }, [isOpen, candidateId, jobId, screeningScore]);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const handleRequestHumanReview = () => {
    logAnnexIIIAuditEvent({
      candidateId,
      jobId,
      screeningScore,
      eventType: ANNEX_III_AUDIT_EVENTS.HUMAN_REVIEW_REQUESTED,
      isHumanReviewed: true,
      humanDecision: "pending",
    });
    setReviewRequested(true);
    setAuditLogs(getAnnexIIIAuditLogs());
    onHumanReviewRequested?.();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
          compact
            ? "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
            : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
        } ${className}`}
        aria-label="EU AI Act Annex III Recruitment Screening Compliance Information"
        data-compliance="eu-ai-act-annex-iii"
        title="EU AI Act Annex III Compliance — Click for Candidate Rights & Transparency Notice"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="text-blue-600 dark:text-blue-400"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
        <span className="font-semibold text-blue-700 dark:text-blue-300">
          EU AI Act Annex III
        </span>
        <span className="opacity-80">|</span>
        <span>Candidate Screening AI</span>
        {screeningScore !== undefined && (
          <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 font-mono text-[10px] text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {screeningScore}% Match
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="annex3-modal-title"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex items-center gap-2.5">
                <div className="rounded-lg bg-blue-100 p-2 text-blue-600 dark:bg-blue-950 dark:text-blue-400">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h2
                    id="annex3-modal-title"
                    className="text-lg font-bold text-slate-900 dark:text-slate-100"
                  >
                    EU AI Act Compliance & Candidate Rights
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Annex III Recruitment & Candidate Screening Transparency
                    (Regulation 2024/1689)
                  </p>
                </div>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-md p-1 text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:hover:text-slate-200"
                aria-label="Close modal"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-5 overflow-y-auto p-6 text-sm text-slate-700 dark:text-slate-300">
              <div className="rounded-lg border border-blue-200/80 bg-blue-50/70 p-4 dark:border-blue-800/80 dark:bg-blue-950/40">
                <div className="mb-1 flex items-center gap-2 font-semibold text-blue-900 dark:text-blue-200">
                  <span>EU AI Act Annex III Classification</span>
                  <span className="rounded bg-blue-200 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-blue-900 dark:bg-blue-900 dark:text-blue-100">
                    High-Risk System Controls Active
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-blue-800 dark:text-blue-300">
                  Under EU AI Act Annex III Section 4, AI systems used for
                  recruitment, screening, or evaluating candidates in job
                  applications are subject to mandatory transparency, human
                  oversight, and audit logging.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5 dark:border-slate-800 dark:bg-slate-900/50">
                  <h3 className="mb-1.5 flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
                    <svg
                      className="h-4 w-4 text-emerald-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    Human-in-the-Loop Oversight
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    All AI match scores and skill analysis are advisory
                    recommendations only. Qualified human recruiters perform
                    candidate selection decisions.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5 dark:border-slate-800 dark:bg-slate-900/50">
                  <h3 className="mb-1.5 flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
                    <svg
                      className="h-4 w-4 text-amber-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Candidate Right to Explanation
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    Pursuant to Article 86 and Article 14, candidates have the
                    right to receive clear explanations of AI evaluation
                    parameters and request human review.
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center justify-between gap-3 rounded-lg bg-slate-100 p-4 dark:bg-slate-800/60 sm:flex-row">
                <div>
                  <h4 className="text-xs font-medium text-slate-900 dark:text-slate-100">
                    Request Human Review
                  </h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Bypass AI evaluation and request manual human review of
                    your profile.
                  </p>
                </div>

                {reviewRequested ? (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                    Human Review Requested
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleRequestHumanReview}
                    className="rounded-md bg-blue-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    Request Human Review
                  </button>
                )}
              </div>

              <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowAuditLogs(!showAuditLogs)}
                  className="flex w-full items-center justify-between text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <span className="flex items-center gap-1.5 font-semibold">
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
                    </svg>
                    EU AI Act Audit Log ({auditLogs.length} events recorded)
                  </span>
                  <span>{showAuditLogs ? "Hide ▲" : "Show ▼"}</span>
                </button>

                {showAuditLogs && (
                  <div className="mt-3 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] text-slate-300">
                    {auditLogs.length === 0 ? (
                      <p className="italic text-slate-500">
                        No audit records logged yet.
                      </p>
                    ) : (
                      auditLogs.map((log) => (
                        <div
                          key={log.id}
                          className="border-b border-slate-800/80 pb-2 last:border-0"
                        >
                          <div className="flex items-center justify-between text-[10px] text-blue-400">
                            <span>[{log.id}]</span>
                            <span>
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <div className="font-semibold text-slate-200">
                            {log.eventType}
                          </div>
                          <div className="text-[10px] text-slate-400">
                            Scope: {log.complianceScope} | Model: {log.modelId} |
                            Human: {log.isHumanReviewed ? "Yes" : "No"}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3 text-[11px] text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
              <span>Alchema Compliance Engine • EU AI Act 2024/1689</span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded bg-slate-200 px-3 py-1 font-medium text-slate-800 transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
